import React, { useState, useEffect, useCallback } from "react";
import { PageHeader } from "@ant-design/pro-layout";
import {
  Layout,
  Tabs,
  Row,
  Col,
  Select,
  Button,
  Card,
  Alert,
  Space,
  Spin,
  Typography,
  Popconfirm,
  message,
  Empty,
  Progress,
  Table,
  Tag,
} from "antd";
import {
  AndroidOutlined,
  AppleOutlined,
  LinkOutlined,
  DeleteOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { Platform } from "../../types/platform";
import { WizardService } from "../../services/WizardService";
import {
  AppodealVersion,
  NetworkStatus,
  Coverage,
} from "../../types/recommendations";

const { Content } = Layout;
const { Text } = Typography;

export const RecommendationsPage: React.FC = () => {
  const [platform, setPlatform] = useState<Platform>(Platform.ANDROID);
  const [appodealVersions, setAppodealVersions] = useState<AppodealVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<number | undefined>(undefined);
  const [networks, setNetworks] = useState<NetworkStatus[]>([]);
  const [coverage, setCoverage] = useState<Coverage>({ totalNetworks: 0, withRecommendations: 0, missing: 0 });
  const [copySourceSdkId, setCopySourceSdkId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAppodealVersions = useCallback(async () => {
    setVersionsLoading(true);
    setSelectedVersionId(undefined);
    setNetworks([]);
    setError(null);

    try {
      const response = await WizardService.Recommendations.getAppodealVersions();
      const versions = platform === Platform.ANDROID ? response.android : response.ios;
      setAppodealVersions(versions);
      if (versions.length > 0) {
        setSelectedVersionId(versions[0].id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load Appodeal versions");
    } finally {
      setVersionsLoading(false);
    }
  }, [platform]);

  useEffect(() => {
    loadAppodealVersions();
  }, [loadAppodealVersions]);

  const loadRecommendations = useCallback(async () => {
    if (!selectedVersionId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await WizardService.Recommendations.getRecommendations(selectedVersionId);
      setNetworks(response.networks);
      setCoverage(response.coverage);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load recommendations");
    } finally {
      setLoading(false);
    }
  }, [selectedVersionId]);

  useEffect(() => {
    loadRecommendations();
  }, [loadRecommendations]);

  const handleAddRecommendation = async (network: NetworkStatus, sdkId: number) => {
    if (!selectedVersionId) return;

    try {
      await WizardService.Recommendations.create({
        appodealSdkId: selectedVersionId,
        sdkId: sdkId,
      });
      message.success(`Recommendation added for ${network.displayName}`);
      await loadRecommendations();
    } catch (err: any) {
      message.error(err.response?.data?.message || "Failed to add recommendation");
    }
  };

  const handleDeleteRecommendation = async (network: NetworkStatus) => {
    if (!network.recommendation) return;

    try {
      await WizardService.Recommendations.delete(network.recommendation.recommendationId);
      message.success(`Recommendation removed for ${network.displayName}`);
      await loadRecommendations();
    } catch (err: any) {
      message.error(err.response?.data?.message || "Failed to delete recommendation");
    }
  };

  const handleCopyRecommendations = async () => {
    if (!selectedVersionId || !copySourceSdkId) return;

    try {
      const response = await WizardService.Recommendations.copyFrom(selectedVersionId, copySourceSdkId);
      message.success(
        `Copied ${response.copied} recommendations. Skipped ${response.skipped} (${response.details.skippedNotFound.length} not found, ${response.details.skippedAlreadyExists.length} already exist)`
      );
      setCopySourceSdkId(undefined);
      await loadRecommendations();
    } catch (err: any) {
      message.error(err.response?.data?.message || "Failed to copy recommendations");
    }
  };

  const copySourceOptions = appodealVersions
    .filter((v) => v.id !== selectedVersionId)
    .map((v) => ({
      value: v.id,
      label: `${v.version} (${v.recommendationsCount} recommendations)`,
    }));

  const coveragePercent = coverage.totalNetworks > 0
    ? Math.round((coverage.withRecommendations / coverage.totalNetworks) * 100)
    : 0;

  const columns = [
    {
      title: "Network",
      key: "network",
      render: (_: unknown, record: NetworkStatus) => (
        <Space>
          {record.hasRecommendation ? (
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
          ) : (
            <WarningOutlined style={{ color: "#faad14", fontSize: 18 }} />
          )}
          <Text strong>{record.displayName}</Text>
        </Space>
      ),
    },
    {
      title: "SDK",
      key: "sdk",
      render: (_: unknown, record: NetworkStatus) => {
        if (record.hasRecommendation && record.recommendation) {
          return (
            <Space>
              <Text>{record.recommendation.sdk.name}</Text>
              <Tag color="blue">{record.recommendation.sdk.version}</Tag>
            </Space>
          );
        } else if (record.availableVersions && record.availableVersions.length > 0) {
          return (
            <Select
              style={{ width: 300 }}
              placeholder="Select version"
              options={record.availableVersions.map((v) => ({
                value: v.id,
                label: `${v.name} ${v.version}`,
              }))}
              onChange={(sdkId) => handleAddRecommendation(record, sdkId)}
            />
          );
        } else {
          return <Text type="secondary">No versions available</Text>;
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      render: (_: unknown, record: NetworkStatus) => {
        if (record.hasRecommendation && record.recommendation) {
          return (
            <Space>
              <Link to={`/wizard/sdk/${record.recommendation.sdk.id}`}>
                <Button type="link" icon={<LinkOutlined />} size="small">
                  View
                </Button>
              </Link>
              <Popconfirm
                title="Remove recommendation?"
                description={`Remove ${record.displayName} recommendation?`}
                onConfirm={() => handleDeleteRecommendation(record)}
                okText="Remove"
                cancelText="Cancel"
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                />
              </Popconfirm>
            </Space>
          );
        } else if (record.availableVersions && record.availableVersions.length > 0) {
          return null;
        } else {
          return <Text type="secondary">—</Text>;
        }
      },
    },
  ];

  return (
    <Layout>
      <Content>
        <PageHeader title="Recommended SDK Management" />

        <Tabs
          activeKey={platform}
          onChange={(key) => setPlatform(key as Platform)}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: Platform.ANDROID,
              label: (
                <Space>
                  <AndroidOutlined /> Android
                </Space>
              ),
            },
            {
              key: Platform.IOS,
              label: (
                <Space>
                  <AppleOutlined /> iOS
                </Space>
              ),
            },
          ]}
        />

        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Version
              </Text>
              <Select
                style={{ width: "100%" }}
                value={selectedVersionId}
                onChange={(value) => setSelectedVersionId(value)}
                loading={versionsLoading}
                disabled={versionsLoading || appodealVersions.length === 0}
                options={appodealVersions.map((v) => ({
                  value: v.id,
                  label: `${v.version} (${v.recommendationsCount} recommendations)`,
                }))}
                placeholder={versionsLoading ? "Loading..." : "Select version"}
              />
            </Col>
            <Col span={16}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Copy from...
              </Text>
              <Space.Compact style={{ width: "100%" }}>
                <Select
                  style={{ width: "calc(100% - 80px)" }}
                  placeholder="Select source version"
                  options={copySourceOptions}
                  disabled={!selectedVersionId || copySourceOptions.length === 0}
                  onChange={(value) => setCopySourceSdkId(value)}
                  value={copySourceSdkId}
                />
                <Button
                  icon={<CopyOutlined />}
                  disabled={!selectedVersionId || !copySourceSdkId || copySourceOptions.length === 0}
                  onClick={handleCopyRecommendations}
                >
                  Copy
                </Button>
              </Space.Compact>
            </Col>
          </Row>
        </Card>

        {error && (
          <Alert
            type="error"
            message="Error"
            description={error}
            showIcon
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 16 }}
          />
        )}

        <Card style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <div>
              <Text strong>Coverage: </Text>
              <Text>
                {coverage.withRecommendations}/{coverage.totalNetworks} networks ({coveragePercent}%)
              </Text>
            </div>
            <Progress
              percent={coveragePercent}
              strokeColor={{
                "0%": "#108ee9",
                "100%": "#87d068",
              }}
              status="active"
            />
            <div>
              <Text type="secondary">
                {coverage.withRecommendations} configured | {coverage.missing} missing
              </Text>
            </div>
          </Space>
        </Card>

        <Card>
          {loading ? (
            <div style={{ textAlign: "center", padding: 48 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Loading networks...</Text>
              </div>
            </div>
          ) : networks.length === 0 ? (
            <Empty description="No networks found" />
          ) : (
            <Table
              dataSource={networks}
              columns={columns}
              rowKey="network"
              pagination={false}
            />
          )}
        </Card>
      </Content>
    </Layout>
  );
};
